import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User, Calendar, Stethoscope, CreditCard, Loader2, Check } from "lucide-react";
import Header from '../../components/ui/Header';
import ConsultationDetails from "./components/ConsultationDetails";
import DoctorSelector from "./components/DoctorSelector";
import ConsultationDateTime from "./components/ConsultationDateTime";
import UnifiedPaymentConfirmation from "../../components/booking/UnifiedPaymentConfirmation";
import UnifiedBookingSuccess from "../../components/booking/UnifiedBookingSuccess";

const SpecialistBookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef(null);
  const paymentConfirmationRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentValidationState, setPaymentValidationState] = useState(false);
  const [bookingData, setBookingData] = useState({
    treatmentId: location?.state?.treatmentId || null,
    treatmentName: location?.state?.treatmentName || '',
    details: {
      name: "",
      email: "",
      phone: "",
      gender: "",
      chiefComplaint: "",
      medicalHistory: "",
      currentMedications: "",
      severity: "moderate",
      consultationMode: "in_person",
      reminderPreferences: [],
    },
    doctor: null,
    dateTime: {
      date: null,
      timeSlot: null,
    },
    payment: {
      method: "",
      status: "pending",
    },
  });

  // RE-ENABLED - Scroll to top whenever step changes (SAFE - just scrolls the page)
  useEffect(() => {
    if (contentRef?.current) {
      contentRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentStep]);



  const steps = [
    { id: 1, title: "Details", component: ConsultationDetails, icon: User },
    { id: 2, title: "Select Doctor", component: DoctorSelector, icon: Stethoscope },
    { id: 3, title: "Date & Time", component: ConsultationDateTime, icon: Calendar },
    { id: 4, title: "Payment", component: UnifiedPaymentConfirmation, icon: CreditCard },
    { id: 5, title: "Confirmation", component: UnifiedBookingSuccess, icon: CreditCard },
  ];

  const CurrentStepComponent = steps?.find(
    (step) => step?.id === currentStep
  )?.component;

  const handleNext = async () => {
    console.log(`📍 handleNext called - currentStep: ${currentStep}, isProcessingPayment: ${isProcessingPayment}`);

    // For step 4 (Payment), trigger the confirmation handler from the child component
    // But only if not already processing (to avoid infinite loop)
    if (currentStep === 4 && paymentConfirmationRef.current && !isProcessingPayment) {
      console.log('💳 Triggering payment confirmation...');
      setIsProcessingPayment(true);
      try {
        await paymentConfirmationRef.current.handleConfirmBooking();
        // SUCCESS - Move to next step (confirmation page)
        console.log('✅ Booking confirmed, moving to step 5');
        setCurrentStep(5);
      } catch (error) {
        console.error('❌ Error during booking confirmation:', error);
        // Don't move to next step on error
      } finally {
        setIsProcessingPayment(false);
      }
      return;
    }

    // If already processing or not on step 4, just move to next step
    console.log(`➡️ Moving to next step: ${currentStep} -> ${currentStep + 1}`);
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDataUpdate = (stepData) => {
    setBookingData((prev) => ({
      ...prev,
      ...stepData,
    }));
  };

  const handleValidationChange = (isValid) => {
    setPaymentValidationState(isValid);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return (
          bookingData?.details?.name &&
          bookingData?.details?.phone &&
          bookingData?.details?.gender &&
          bookingData?.details?.chiefComplaint
        );
      case 2:
        return bookingData?.doctor !== null;
      case 3:
        return bookingData?.dateTime?.date && bookingData?.dateTime?.timeSlot;
      case 4:
        // Use state that gets updated when checkbox/payment method changes
        return paymentValidationState;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div ref={contentRef} className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 md:py-8">
        {/* Page Title */}
        <div className="mt-[60px] mb-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-purple-800 mb-2">
              Book Doctor Consultation
            </h1>
            {bookingData?.treatmentName && (
              <p className="text-lg text-gray-600">
                For {bookingData?.treatmentName}
              </p>
            )}
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6 sm:mb-8">
          <div className="max-w-4xl mx-auto bg-background border-2 border-purple-200 rounded-lg shadow-md p-4 sm:p-6 md:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-2">
              {steps?.slice(0, 4)?.map((step, index) => {
                const StepIcon = step?.icon;
                return (
                  <React.Fragment key={step?.id}>
                    <div className="flex items-center w-full sm:w-auto sm:flex-1">
                      <div className="flex flex-col items-center w-full">
                        <div
                          className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-semibold transition-all flex-shrink-0 ${currentStep >= step?.id
                            ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-500"
                            }`}
                        >
                          <StepIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                        <span
                          className={`mt-2 text-xs sm:text-sm font-medium transition-colors text-center whitespace-nowrap ${currentStep >= step?.id
                            ? "text-purple-600" : "text-gray-500"
                            }`}
                        >
                          {step?.title}
                        </span>
                      </div>
                    </div>
                    {index < 3 && (
                      <div className="hidden sm:flex items-center sm:w-16 md:w-24">
                        <div
                          className={`h-1 w-full transition-all ${currentStep > step?.id ? "bg-purple-600" : "bg-gray-200"
                            }`}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="max-w-4xl mx-auto glass-card p-4 sm:p-6 md:p-8">
          {CurrentStepComponent && (
            <>
              {currentStep === 4 ? (
                <CurrentStepComponent
                  ref={paymentConfirmationRef}
                  bookingData={bookingData}
                  onDataUpdate={handleDataUpdate}
                  onBack={handleBack}
                  onValidationChange={handleValidationChange}
                  bookingType="consultation"
                />
              ) : (
                <CurrentStepComponent
                  bookingData={bookingData}
                  onDataUpdate={handleDataUpdate}
                  onNext={handleNext}
                  onBack={handleBack}
                  bookingType={(currentStep === 5) ? "consultation" : undefined}
                />
              )}
            </>
          )}

          {/* Navigation Buttons */}
          {currentStep < 5 && (
            <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t">
              <button
                onClick={handleBack}
                disabled={currentStep === 1 || isProcessingPayment}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-all text-sm sm:text-base ${currentStep === 1 || isProcessingPayment
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
              >
                Back
              </button>
              <button
                onClick={handleNext}
                disabled={!canProceed() || isProcessingPayment}
                className={`w-full sm:w-auto px-6 py-3 rounded-lg font-semibold transition-all text-sm sm:text-base flex items-center justify-center ${canProceed() && !isProcessingPayment
                  ? "bg-purple-600 text-white hover:bg-purple-700" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
              >
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : currentStep === 4 ? (
                  <>
                    Confirm Booking
                    <Check className="w-5 h-5 ml-2" />
                  </>
                ) : (
                  "Next"
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpecialistBookingPage;